import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Notification, Title } from '@xsolla/ui-core';

import { LoginProjectConfiguration, patchFineConfigurations } from '../../../../api';
import { useNotifications } from '../../../../hooks/useNotifications';
import { usePaths } from '../../../../routes/routesHelper';
import { FormFooter, Notice, RadioGroup } from '../../../ui';
import { PasswordlessMethodChangeWrapper, RadioWrapper, Subtitle, Wrapper } from './style';

enum PasswordlessTypes {
  phone = 'phone',
  email = 'email',
  'phoneEmail' = 'phoneEmail'
}

type Props = RouteComponentProps & {
  currentProject: LoginProjectConfiguration | null;
};

const PasswordlessType = ({ history, currentProject }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const [activeType, setActiveType] = useState<PasswordlessTypes | null>(null);
  const [fetching, setFetching] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const [error, setError] = useState<boolean>(false);

  const onClose = () =>
    currentProject && history.push({ pathname: navigationPath(currentProject.id) });

  useEffect(() => {
    if (currentProject?.is_email_auth_allowed && currentProject?.is_phone_auth_allowed) {
      setActiveType(PasswordlessTypes.phoneEmail);
      return;
    }

    if (currentProject?.is_phone_auth_allowed) {
      setActiveType(PasswordlessTypes.phone);
      return;
    }

    setActiveType(PasswordlessTypes.email);
  }, [currentProject]);

  const onSubmit = () => {
    const newConfigurations = currentProject;
    if (!newConfigurations) {
      return;
    }

    setFetching(true);
    setError(false);
    switch (activeType) {
      case PasswordlessTypes.phoneEmail: {
        newConfigurations.is_phone_auth_allowed = true;
        newConfigurations.is_email_auth_allowed = true;
        break;
      }
      case PasswordlessTypes.phone: {
        newConfigurations.is_phone_auth_allowed = true;
        newConfigurations.is_email_auth_allowed = false;
        break;
      }
      case PasswordlessTypes.email: {
        newConfigurations.is_phone_auth_allowed = false;
        newConfigurations.is_email_auth_allowed = true;
        break;
      }
      default: {
        newConfigurations.is_phone_auth_allowed = false;
        newConfigurations.is_email_auth_allowed = true;
      }
    }

    patchFineConfigurations(newConfigurations, currentProject.id)
      .then(() => showSuccessNotify())
      .catch(() => {
        showErrorNotify();
        setError(true);
      })
      .finally(() => setFetching(false));
  };

  return (
    <>
      <PasswordlessMethodChangeWrapper>
        <Wrapper>
          <Title center>
            {intl.formatMessage({ id: 'settings.crumbs.login-type.passwordless-login' })}
          </Title>
          <Subtitle>
            {intl.formatMessage({ id: 'settings.passwordless.change-method.description' })}
          </Subtitle>
          <Notification icon={Notice}>
            {intl.formatMessage({ id: 'settings.passwordless.change-method.notification' })}
          </Notification>

          <RadioWrapper>
            <RadioGroup
              name="passwordless-type"
              defaultValue={activeType || ''}
              onChange={(e) => setActiveType(e.target.value as PasswordlessTypes)}
              options={Object.keys(PasswordlessTypes).map((methodName) => ({
                label: intl.messages[`settings.passwordless.change-method.${methodName}.label`],
                description:
                  intl.messages[`settings.passwordless.change-method.${methodName}.description`],
                radioValue: methodName
              }))}
            />
          </RadioWrapper>
        </Wrapper>
      </PasswordlessMethodChangeWrapper>

      <FormFooter onSubmit={onSubmit} onCancel={onClose} fetching={fetching} error={error} />
    </>
  );
};

export default withRouter(PasswordlessType);
